import { useCallback, useContext, useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import axios from 'axios';
import StyleAttributeTable from '../../components/StyleAttributeTable';
import { AuthContext } from '../../context/AuthContext';
import Spinner from '../../components/Spinner';
import DeleteModal from '../modals/DeleteModal';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const MaterialManagement = ({ embedded, styleNumber, companyCode, onSuccess, onCancel }) => {
  const { userData } = useContext(AuthContext);
  const [materials, setMaterials] = useState([]);
  const [pageLoading, setPageLoading] = useState(true); // Page loading state
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    material_name: '',
    description: ''
  });
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteMaterialId, setDeleteMaterialId] = useState(null);

  const columns = [
    { key: 'material_name', label: 'Material Name' },
    { key: 'description', label: 'Description' }
  ];

  const fetchMaterials = useCallback(async () => {
    setPageLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/api/admin/materials/get-materials`, {
        params: { company_code: userData.company_code }
      });
      const data = response.data;
      if (data.success) {
        setMaterials(data.materials);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Error fetching materials');
    }
    setPageLoading(false);
  }, [userData.company_code]);

  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = isEditing 
        ? `${BASE_URL}/api/admin/materials/update-materials/${editingId}`
        : `${BASE_URL}/api/admin/materials/add-materials`;
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          company_code: companyCode || userData.company_code
        }),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(isEditing ? 'Material updated successfully' : 'Material added successfully');
        fetchMaterials();
        if (!isEditing && embedded && styleNumber && companyCode) {
          // Assign to style
          let materialId = null;
          if (data.material && (data.material.material_id || data.material.id)) {
            materialId = data.material.material_id || data.material.id;
          } else if (data.id) {
            materialId = data.id;
          } else if (data.material_id) {
            materialId = data.material_id;
          }
          if (materialId) {
            const assignRes = await fetch(`${BASE_URL}/api/admin/styles/add-style-attributes`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                style_number: styleNumber,
                company_code: companyCode,
                type: 'materials',
                attribute_ids: [materialId]
              })
            });
            const assignData = await assignRes.json();
          } else {
            console.warn('Could not determine new material ID from response:', data);
          }
        }
        resetForm();
        if (onSuccess) onSuccess();
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Error saving material');
    }
  };

  const handleEdit = (material) => {
    setIsEditing(true);
    setEditingId(material.material_id);
    setFormData({
      material_name: material.material_name,
      description: material.description || ''
    });
  };

  const handleDeleteClick = (materialId) => {
    setDeleteMaterialId(materialId);
    setShowDeleteModal(true);
  };

  const handleDeleteSuccess = () => {
    setSuccess('Material deleted successfully');
    fetchMaterials();
  };

  const handleDeleteError = (error) => {
    setError(error?.message || 'Error deleting material');
  };

  const resetForm = () => {
    setFormData({ material_name: '', description: '' });
    setIsEditing(false);
    setEditingId(null);
  };

  return (
    <>
      {pageLoading ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px' 
        }}>
          <Spinner />
        </div>
      ) : (
        <Container>
          <StyleAttributeTable 
            title="Materials"
            items={materials}
            columns={columns}
            formData={formData}
            setFormData={setFormData}
            handleSubmit={handleSubmit}
            handleEdit={handleEdit}
            handleDelete={handleDeleteClick}
            isEditing={isEditing}
            error={error}
            success={success}
            onCancel={resetForm}
            embedded={embedded}
          />
          
          {/* Delete Confirmation Modal */}
          <DeleteModal
            show={showDeleteModal}
            onHide={() => setShowDeleteModal(false)}
            id={deleteMaterialId}
            deleteUrl={`/api/admin/materials/delete-materials/:id`}
            entityLabel="material"
            modalTitle="Delete Material"
            onDeleteSuccess={handleDeleteSuccess}
            onDeleteError={handleDeleteError}
          />
        </Container>
      )}
    </>
  );
};

export default MaterialManagement;
